import { useEffect, useRef, useState } from "react";
import { Loader2, ExternalLink } from "lucide-react";

/**
 * Embed third-party forms (Jotform, Google Forms, Typeform, Microsoft Forms,
 * or any other iframe-able URL) directly on a FIC page so the visitor never
 * feels redirected away.
 *
 * Provider-specific behaviour:
 *   - Jotform  → listens for the form's postMessage('setHeight:NNN:formID')
 *                broadcast and resizes the iframe to match. No inner scroll.
 *   - Google Forms → strips the Google header/footer chrome via the
 *                `embedded=true` query flag and uses a generous default
 *                height. Google Forms is paginated so this is plenty.
 *   - Typeform / generic → reasonable default height, scrolls inside the
 *                iframe if needed. Stays full-width and mobile-friendly.
 */
export default function FormEmbed({ url, title }) {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [height, setHeight] = useState(900); // sensible default; Jotform will override

  const provider = detectProvider(url);
  const finalUrl = normalizeUrl(url, provider);

  // Jotform broadcasts `setHeight:HHHH:formID` via postMessage as the form
  // grows/shrinks. Subscribe + sync. We also use the height change as a
  // signal that the user clicked Next/Back/Save and we should scroll the
  // top of the form back into view — otherwise users get stranded mid-page.
  const lastHeightRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    if (provider !== "jotform") return undefined;
    const onMsg = (e) => {
      // Only trust messages coming from Jotform's own origins. Prevents any
      // other embedded iframe on the page (or a compromised third-party
      // script) from spoofing setHeight and pushing content around.
      const origin = (e.origin || "").toLowerCase();
      if (!/(^https?:\/\/(?:[a-z0-9-]+\.)?jotform\.com$)/.test(origin)) return;
      const data = typeof e.data === "string" ? e.data : "";
      if (!data || !data.startsWith("setHeight")) return;
      const parts = data.split(":");
      const h = parseInt(parts[1], 10);
      if (!Number.isFinite(h) || h <= 100) return;
      const newH = h + 20;
      setHeight(newH);

      // If the height changed meaningfully AND it's not the initial load,
      // the user just navigated to a new form page → scroll the iframe top
      // into view so they don't get stuck in the middle.
      const prev = lastHeightRef.current;
      const changed = Math.abs(newH - prev) > 30;
      if (!isInitialLoadRef.current && changed && iframeRef.current) {
        // Header height offset so the form heading sits below sticky nav.
        scrollIframeIntoView(iframeRef.current);
      }
      lastHeightRef.current = newH;
      isInitialLoadRef.current = false;
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [provider]);

  // For Google / generic providers we set a more conservative default and
  // let the iframe scroll if the form is exceptionally long.
  useEffect(() => {
    if (provider === "google") setHeight(1100);
    else if (provider === "typeform") setHeight(700);
  }, [provider]);

  if (!url) return null;

  return (
    <section className="mt-16" data-testid="page-form-embed">
      {/* Header with girih divider */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-gold font-semibold">
          <span className="h-px w-10 bg-gold/50" />
          {title || "Registration"}
          <span className="h-px w-10 bg-gold/50" />
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-ink mt-3">
          Fill out the form below
        </h2>
        <p className="text-sm text-ink/55 mt-2">
          You'll stay on flintislamiccenter.com — your submission goes directly to us.
        </p>
      </div>

      {/* Form card */}
      <div className="relative rounded-3xl bg-white border border-sand shadow-xl shadow-navy/5 overflow-hidden">
        {/* Top accent strip */}
        <div className="h-1 bg-gradient-to-r from-gold/0 via-gold to-gold/0" />

        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-cream/85 backdrop-blur-sm z-10 pointer-events-none">
            <Loader2 className="animate-spin text-gold" size={32} />
            <p className="mt-3 text-xs uppercase tracking-[0.25em] text-ink/55">Loading form…</p>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={finalUrl}
          title={title || "Embedded form"}
          loading="lazy"
          onLoad={() => setLoading(false)}
          allow="clipboard-write"
          allowFullScreen
          style={{ height: `${height}px`, transition: "height 360ms ease" }}
          className="w-full border-0 block bg-white"
          data-testid="page-form-embed-iframe"
        />
      </div>

      {/* Fallback open-in-new-tab link */}
      <div className="mt-4 text-center">
        <a
          href={finalUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1.5 text-xs text-ink/50 hover:text-navy"
        >
          Having trouble? Open the form in a new tab <ExternalLink size={11} />
        </a>
      </div>
    </section>
  );
}

/* ------------------------ helpers ------------------------ */

function detectProvider(url) {
  if (!url) return "generic";
  const u = url.toLowerCase();
  if (u.includes("jotform.com") || u.includes("form.jotform")) return "jotform";
  if (u.includes("docs.google.com/forms") || u.includes("forms.gle")) return "google";
  if (u.includes("typeform.com")) return "typeform";
  if (u.includes("forms.office.com") || u.includes("forms.microsoft.com")) return "microsoft";
  return "generic";
}

/**
 * Normalize a user-pasted form URL to an embed-ready URL.
 * Admins often paste the *share* link from Jotform/Google etc. — we adapt
 * it so the iframe works without their needing to know the embed URL.
 */
function normalizeUrl(url, provider) {
  if (!url) return url;
  const trimmed = url.trim();

  if (provider === "google") {
    // Convert any /viewform URL to ?embedded=true so Google strips its chrome.
    // Accept short forms.gle links too — Google will redirect to viewform.
    if (trimmed.includes("/viewform")) {
      const sep = trimmed.includes("?") ? "&" : "?";
      if (!/[?&]embedded=true/.test(trimmed)) return `${trimmed}${sep}embedded=true`;
    }
    return trimmed;
  }

  if (provider === "jotform") {
    // Auto-correct common Jotform URL mistakes so admins don't have to
    // remember the difference between the EDITOR URL and the EMBED URL.
    //
    //   ❌ https://www.jotform.com/build/{id}   (editor — returns 403 Forbidden)
    //   ❌ https://www.jotform.com/edit/{id}    (editor — returns 403)
    //   ❌ https://eu.jotform.com/build/{id}
    //   ❌ https://www.jotform.com/form/{id}    (preview-only)
    //   ✅ https://form.jotform.com/{id}        (public embed)
    //
    // We accept any of the above and rewrite to the public embed URL.
    const idMatch = trimmed.match(/jotform\.com\/(?:build|edit|form)\/(\d+)/i);
    if (idMatch) {
      return `https://form.jotform.com/${idMatch[1]}`;
    }
    // If the URL is already in the canonical form.jotform.com format, pass through.
    return trimmed;
  }

  return trimmed;
}

/**
 * Scroll the top of the iframe (i.e. the form's first field on the new page)
 * back into the viewport. Accounts for the site's sticky header height so
 * the heading isn't hidden behind it.
 */
function scrollIframeIntoView(iframeEl) {
  if (!iframeEl) return;
  const rect = iframeEl.getBoundingClientRect();
  const stickyOffset = 90; // approximate header height
  const targetY = window.scrollY + rect.top - stickyOffset;
  // Avoid scrolling DOWN when the form is already above the viewport.
  if (rect.top > 0 - 20 && rect.top < stickyOffset + 20) return;
  window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
}
